/**
 * SectionRegenWrap — internal helper for Strategy Insights sections.
 *
 * Each section has a Re-synthesize action wrapped in Regenerable. This small
 * helper centralises that pattern so each section component only supplies
 * its title, body renderer, and re-synth handler.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Sparkle } from '@phosphor-icons/react';
import { Regenerable } from '@/components/shared/Regenerable';
import { AIActionButton } from '@/components/shared/AIActionButton';
import { DockedCollapsible } from '@/components/shared/DockedCollapsible';
import { copy } from '@/content/copy';
import { DS_SPACING } from '@/config/ui-invariants';

const DEFAULT_STEPS = [
  { key: 'read', label: 'Reading', state: 'idle' },
  { key: 'synthesize', label: 'Synthesizing', state: 'idle' },
];

export function SectionRegenWrap({
  title,
  defaultOpen = false,
  onResynth,
  children,
  changedKeys = [],
}) {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [steps, setSteps] = useState(DEFAULT_STEPS);
  const [keys, setKeys] = useState(changedKeys);

  const handleClick = async () => {
    if (typeof onResynth !== 'function') return;
    setIsRegenerating(true);
    setSteps([
      { key: 'read', label: 'Reading', state: 'active' },
      { key: 'synthesize', label: 'Synthesizing', state: 'idle' },
    ]);
    try {
      const result = await onResynth();
      setSteps([
        { key: 'read', label: 'Reading', state: 'complete' },
        { key: 'synthesize', label: 'Synthesizing', state: 'complete' },
      ]);
      setKeys(Array.isArray(result?.changedKeys) ? result.changedKeys : []);
    } catch (_err) {
      setSteps((prev) => prev.map((s) => (s.state === 'active' ? { ...s, state: 'error' } : s)));
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <DockedCollapsible title={title} defaultOpen={defaultOpen}>
      <div style={{ marginBottom: DS_SPACING.cardGap }}>
        <Regenerable
          isRegenerating={isRegenerating}
          steps={steps}
          changedKeys={keys}
          regenerateLabel={copy.strategyInsights.errors ? copy.shared.regenerating : copy.shared.regenerating}
        >
          {children}
        </Regenerable>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <AIActionButton icon={<Sparkle size={16} weight="fill" />} onClick={handleClick}>
          {copy.strategyInsights.actions.resynth}
        </AIActionButton>
      </div>
    </DockedCollapsible>
  );
}

SectionRegenWrap.propTypes = {
  title: PropTypes.string.isRequired,
  defaultOpen: PropTypes.bool,
  onResynth: PropTypes.func,
  children: PropTypes.node,
  changedKeys: PropTypes.arrayOf(PropTypes.string),
};

export default SectionRegenWrap;
